import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { FileText, FileSpreadsheet, RefreshCw, Package } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { API_URL, todayLuanda } from "../lib/utils";
import { openPdfDocument } from "../lib/docViewer";
import { downloadBlob } from "../lib/cashReportPdf";
import { ReportFilterBar } from "../components/reports/ReportFilterBar";
import { ProductSalesKpis, ProductSalesReport } from "../components/reports/ProductSalesReport";

const qs = (f) => new URLSearchParams({ start: f.start, end: f.end, include_unsold: String(f.include_unsold), ...(f.category_id ? { category_id: f.category_id } : {}) }).toString();
const fmtDay = (d) => d.split("-").reverse().join("/");

export default function ReportCenterPage() {
  const { token } = useAuth();
  const headers = token ? { Authorization: `Bearer ${token}` } : {};
  const [filters, setFilters] = useState({ start: todayLuanda(), end: todayLuanda(), category_id: "", include_unsold: true });
  const [categories, setCategories] = useState([]);
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState("");

  useEffect(() => {
    axios.get(`${API_URL}/categories`, { headers, withCredentials: true }).then((r) => setCategories(r.data || [])).catch(() => {});
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const load = useCallback(async () => {
    if (!filters.start || !filters.end) return;
    setLoading(true);
    try {
      const r = await axios.get(`${API_URL}/reports/product-sales?${qs(filters)}`, { headers, withCredentials: true });
      setReport(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erro ao gerar relatório");
    } finally {
      setLoading(false);
    }
  }, [filters]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { load(); }, [load]);

  const exportFile = async (kind) => {
    setExporting(kind);
    try {
      const r = await axios.get(`${API_URL}/reports/product-sales.${kind}?${qs(filters)}`, { headers, withCredentials: true, responseType: "blob" });
      const name = `produtos-vendidos-${filters.start}_${filters.end}.${kind}`;
      if (kind === "pdf") openPdfDocument(new Blob([r.data], { type: "application/pdf" }), { title: "Relatório de produtos vendidos", filename: name });
      else { downloadBlob(new Blob([r.data]), name); toast.success("Excel gerado"); }
    } catch {
      toast.error(`Erro ao gerar ${kind.toUpperCase()}`);
    } finally {
      setExporting("");
    }
  };

  const periodLabel = filters.start === filters.end ? fmtDay(filters.start) : `${fmtDay(filters.start)} a ${fmtDay(filters.end)}`;

  return (
    <div className="space-y-5 animate-in fade-in duration-300" data-testid="report-center-page">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-3">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold font-['Outfit'] flex items-center gap-2"><Package className="w-7 h-7 text-primary" /> Gerar Relatórios</h1>
          <p className="text-sm text-muted-foreground">Produtos vendidos por categoria — quantidade vendida, valor, margem e stock no sistema · <span className="font-medium text-foreground" data-testid="report-period-label">{periodLabel}</span></p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={load} disabled={loading} className="gap-1.5" data-testid="report-refresh-btn"><RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} /> Actualizar</Button>
          <Button size="sm" onClick={() => exportFile("pdf")} disabled={!report || !!exporting} className="gap-1.5" data-testid="report-pdf-btn"><FileText className="w-4 h-4" /> {exporting === "pdf" ? "A gerar…" : "PDF"}</Button>
          <Button size="sm" variant="secondary" onClick={() => exportFile("xlsx")} disabled={!report || !!exporting} className="gap-1.5" data-testid="report-excel-btn"><FileSpreadsheet className="w-4 h-4" /> {exporting === "xlsx" ? "A gerar…" : "Excel"}</Button>
        </div>
      </div>

      <ReportFilterBar filters={filters} onChange={setFilters} categories={categories} />

      {report && <ProductSalesKpis totals={report.totals} />}
      {loading && !report ? (
        <div className="rounded-xl border p-10 text-center text-sm text-muted-foreground" data-testid="report-loading">A gerar relatório…</div>
      ) : (
        <ProductSalesReport report={report} />
      )}
    </div>
  );
}
